import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CATEGORIES, SECTORS } from "@/data/catalog";
import { useAuth } from "@/context/AuthContext";
import { Plus, Eye, MessageCircle, Users, Loader2, Trash2, Image as ImageIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", category: "home_services", subcategory: "", sector: "F-7",
  address: "", description: "", phone: "", whatsapp: "", email: "", website: "",
  hours: "", price_range: "", photos: [], tags: [], map_link: "",
};

function NativeSelect({ value, onChange, placeholder, options, testid, className = "" }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className={`mt-1 flex h-10 w-full items-center rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600 appearance-none bg-no-repeat ${className}`}
      style={{
        backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b' stroke-width='2'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3e%3c/svg%3e\")",
        backgroundPosition: "right 0.6rem center",
        backgroundSize: "1.1rem",
        paddingRight: "2.2rem",
      }}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function BusinessForm({ initial, onDone, onClose }) {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const cat = CATEGORIES.find((c) => c.key === f.category);

  const set = (k, v) => setF((prev) => ({ ...prev, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photos", [...f.photos, `${process.env.REACT_APP_BACKEND_URL}${data.url}`]);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!f.name || !f.sector || !f.category) { toast.error("Name, category and sector required"); return; }
    setSaving(true);
    try {
      const payload = { ...f };
      if (initial?.business_id) {
        await api.put(`/businesses/${initial.business_id}`, payload);
        toast.success("Listing updated");
      } else {
        await api.post("/businesses", payload);
        toast.success("Listing submitted — pending admin approval");
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
      <div><Label>Business name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1" data-testid="biz-name-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Category</Label>
          <NativeSelect
            value={f.category}
            onChange={(v) => setF((p) => ({ ...p, category: v, subcategory: "" }))}
            testid="biz-category-select"
            options={CATEGORIES.map((c) => ({ value: c.key, label: c.name }))}
          />
        </div>
        <div>
          <Label>Sector</Label>
          <NativeSelect
            value={f.sector}
            onChange={(v) => set("sector", v)}
            testid="biz-sector-select"
            options={SECTORS.map((s) => ({ value: s, label: s }))}
          />
        </div>
      </div>
      {cat && (
        <div>
          <Label>Subcategory</Label>
          <NativeSelect
            value={f.subcategory}
            onChange={(v) => set("subcategory", v)}
            placeholder="Optional"
            options={cat.sub.map((s) => ({ value: s.key, label: s.name }))}
          />
        </div>
      )}
      <div><Label>Description</Label><Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className="mt-1" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Address</Label><Input value={f.address} onChange={(e) => set("address", e.target.value)} className="mt-1" /></div>
        <div><Label>Google Maps link</Label><Input value={f.map_link} onChange={(e) => set("map_link", e.target.value)} className="mt-1" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" placeholder="03xx-xxxxxxx" /></div>
        <div><Label>WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className="mt-1" placeholder="03xx-xxxxxxx" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className="mt-1" /></div>
        <div><Label>Website</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} className="mt-1" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Hours</Label><Input value={f.hours} onChange={(e) => set("hours", e.target.value)} className="mt-1" placeholder="Mon-Sat 9am-9pm" /></div>
        <div>
          <Label>Price range</Label>
          <NativeSelect
            value={f.price_range}
            onChange={(v) => set("price_range", v)}
            placeholder="Optional"
            options={[
              { value: "$", label: "$ — Budget" },
              { value: "$$", label: "$$ — Mid" },
              { value: "$$$", label: "$$$ — Premium" },
            ]}
          />
        </div>
      </div>
      <div>
        <Label>Photos</Label>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {f.photos.map((p, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
              <img src={p} alt="" className="w-full h-full object-cover" />
              <button onClick={() => set("photos", f.photos.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs">×</button>
            </div>
          ))}
          <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-emerald-500 flex flex-col items-center justify-center cursor-pointer text-slate-500 hover:text-emerald-700">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
            <span className="text-[10px] mt-1">Add photo</span>
            <input type="file" accept="image/*" hidden onChange={upload} data-testid="biz-photo-upload" />
          </label>
        </div>
      </div>
      <DialogFooter className="pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="bg-emerald-800 hover:bg-emerald-900" data-testid="biz-save-btn">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save
        </Button>
      </DialogFooter>
    </div>
  );
}

function DealForm({ businessId, onDone, onClose }) {
  const [f, setF] = useState({ title: "", description: "", discount: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.title) return toast.error("Title required");
    setSaving(true);
    try {
      await api.post("/deals", { business_id: businessId, ...f });
      toast.success("Deal published");
      onDone();
    } catch { toast.error("Failed"); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-3">
      <div><Label>Deal title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="mt-1" placeholder="20% off first visit" data-testid="deal-title-input" /></div>
      <div><Label>Discount tag</Label><Input value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} className="mt-1" placeholder="20% OFF" /></div>
      <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1" /></div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving} className="bg-emerald-800 hover:bg-emerald-900" data-testid="deal-save-btn">Publish deal</Button>
      </DialogFooter>
    </div>
  );
}

export default function OwnerDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [biz, setBiz] = useState([]);
  const [leads, setLeads] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editBiz, setEditBiz] = useState(null);
  const [dealFor, setDealFor] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [user, loading, nav]);

  const load = async () => {
    try {
      const [b, l] = await Promise.all([api.get("/my/businesses"), api.get("/my/quotes/leads")]);
      setBiz(b.data); setLeads(l.data);
    } catch {}
  };
  useEffect(() => { if (user) load(); }, [user]);

  if (loading || !user) return <div className="p-16 text-center text-slate-500">Loading…</div>;

  const totalViews = biz.reduce((s, b) => s + (b.views || 0), 0);
  const totalLeads = biz.reduce((s, b) => s + (b.leads || 0), 0);
  const totalWA = biz.reduce((s, b) => s + (b.whatsapp_clicks || 0), 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-emerald-800">Owner dashboard</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>Hey {user.name?.split(" ")[0]} 👋</h1>
        </div>
        <Button onClick={() => { setEditBiz(null); setAddOpen(true); }} className="bg-emerald-800 hover:bg-emerald-900" data-testid="add-business-btn"><Plus className="w-4 h-4 mr-1" /> Add business</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Listings", value: biz.length, icon: ShieldCheck },
          { label: "Views", value: totalViews, icon: Eye },
          { label: "WhatsApp clicks", value: totalWA, icon: MessageCircle },
          { label: "Leads", value: totalLeads, icon: Users },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{s.label}</div>
              <s.icon className="w-4 h-4 text-emerald-700" />
            </div>
            <div className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: "Outfit" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="listings">
        <TabsList>
          <TabsTrigger value="listings" data-testid="tab-listings">My listings</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads ({leads.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="listings" className="mt-4">
          {biz.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/40 p-12 text-center">
              <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>You don't have any listings yet</div>
              <p className="text-slate-600 mt-1">Add your first business to start getting discovered.</p>
              <Button onClick={() => setAddOpen(true)} className="mt-4 bg-emerald-800 hover:bg-emerald-900">Add my first business</Button>
            </div>
          ) : (
            <div className="space-y-3">
              {biz.map((b) => (
                <div key={b.business_id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-4">
                  <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                    {b.photos?.[0] && <img src={b.photos[0]} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/business/${b.slug || b.business_id}`} className="font-semibold text-slate-900 hover:text-emerald-700">{b.name}</Link>
                      {b.status === "pending" && <Badge className="bg-amber-500">Pending</Badge>}
                      {b.status === "approved" && <Badge className="bg-emerald-700">Live</Badge>}
                      {b.status === "rejected" && <Badge variant="destructive">Rejected</Badge>}
                      {b.verified && <Badge variant="outline" className="border-emerald-700 text-emerald-800">Verified</Badge>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{b.sector} · {b.views || 0} views · {b.leads || 0} leads</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setEditBiz(b); setAddOpen(true); }} data-testid={`edit-biz-${b.business_id}`}>Edit</Button>
                    <Button size="sm" variant="outline" onClick={() => setDealFor(b)} data-testid={`add-deal-${b.business_id}`}>+ Deal</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="leads" className="mt-4">
          {leads.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 p-12 text-center text-slate-500">No leads yet.</div>
          ) : (
            <div className="space-y-3">
              {leads.map((l) => (
                <div key={l.quote_id} className="bg-white rounded-xl border border-slate-200 p-4">
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-semibold text-slate-900">{l.name} · {l.phone}</div>
                      <div className="text-xs text-slate-500">{l.sector} · {l.category} · {l.urgency}</div>
                    </div>
                    <a href={`https://wa.me/92${l.phone.replace(/^0/, "").replace(/\D/g, "")}?text=${encodeURIComponent(`Assalam-o-Alaikum ${l.name}, apki request ApnaIslamabad par mili.`)}`} target="_blank" rel="noreferrer" className="wa-green text-white text-sm font-semibold px-3 py-1.5 rounded-lg">Reply on WhatsApp</a>
                  </div>
                  <p className="text-sm text-slate-600 mt-2">{l.description}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>{editBiz ? "Edit listing" : "Add a new business"}</DialogTitle></DialogHeader>
          <BusinessForm initial={editBiz} onDone={() => { setAddOpen(false); load(); }} onClose={() => setAddOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!dealFor} onOpenChange={(o) => !o && setDealFor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>New deal for {dealFor?.name}</DialogTitle></DialogHeader>
          {dealFor && <DealForm businessId={dealFor.business_id} onDone={() => { setDealFor(null); load(); }} onClose={() => setDealFor(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
